import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';

AWS.config.region = 'ap-southeast-2';

const poolData = {
  UserPoolId: 'ap-southeast-2_LTrn599WD',
  ClientId: '41k0c3poiivkb2aivigpjs3jfo'
};

const identityPoolId = "ap-southeast-2:a06cf3e4-2f80-4f0e-bcb6-b3f6c7b8c640";

const userPool = new CognitoUserPool(poolData);

class AuthManager {
  constructor() {
    this.callbacks = [];
    this.credentials = null;
    this.userGroups = [];
    this.currentRole = null;
  }

  onAuthChange(callback) {
    this.callbacks.push(callback);
  }

  notify(user) {
    this.callbacks.forEach(cb => cb(user));
  }

  getCurrentUser() {
    return userPool.getCurrentUser();
  }

  getSession() {
    const user = this.getCurrentUser();
    if (!user) return Promise.reject('No user');
    
    return new Promise((resolve, reject) => {
      user.getSession((err, session) => {
        err ? reject(err) : resolve(session);
      });
    });
  }

  getToken() {
    return this.getSession().then(session => session.getIdToken().getJwtToken());
  }

  async getCredentials() {
    if (!this.credentials || this.credentials.needsRefresh()) {
      const session = await this.getSession();
      const token = session.getIdToken().getJwtToken();
      
      // Extract user groups from ID token
      this.userGroups = this.extractGroupsFromToken(token);
      
      this.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
        Logins: {
          [`cognito-idp.ap-southeast-2.amazonaws.com/${poolData.UserPoolId}`]: token
        }
      });
      
      await new Promise((resolve, reject) => {
        this.credentials.refresh(err => {
          if (err) {
            reject(err);
          } else {
            // Store the assumed role ARN for reference
            this.currentRole = this.credentials.params?.RoleArn || null;
            resolve();
          }
        });
      });
    }
    return this.credentials;
  }

  clearCredentials() {
    this.credentials = null;
    this.userGroups = [];
    this.currentRole = null;
  }

  isAuthenticated() {
    return this.getSession().then(() => true).catch(() => false);
  }

  extractGroupsFromToken(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload['cognito:groups'] || [];
    } catch (e) {
      console.warn('Failed to extract groups from token:', e);
      return [];
    }
  }

  getUserGroups() {
    return this.userGroups;
  }

  getCurrentRole() {
    return this.currentRole;
  }

  hasGroup(groupName) {
    return this.userGroups.includes(groupName);
  }

  isAdmin() {
    return this.hasGroup('admin');
  }

  isResearcher() {
    return this.hasGroup('researcher');
  }

  isUser() {
    return this.hasGroup('user');
  }
}

const auth = new AuthManager();

export const signUp = (username, password, email) => {
  const attributes = email ? [new CognitoUserAttribute({ Name: 'email', Value: email })] : null;
  
  return new Promise((resolve, reject) => {
    userPool.signUp(username, password, attributes, null, (err, result) => {
      err ? reject(err) : resolve(result.user);
    });
  });
};

export const confirmSignUp = (username, code) => {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      err ? reject(err) : resolve(result);
    });
  });
};

export const signIn = (username, password) => {
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });
  const user = new CognitoUser({ Username: username, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: async (result) => {
        try {
          await auth.getCredentials();
          auth.notify(user);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      },
      onFailure: reject,
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        reject({ type: 'NEW_PASSWORD_REQUIRED', user, userAttributes, requiredAttributes });
      },
      totpRequired: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_REQUIRED', user, challengeName, challengeParameters });
      },
      mfaRequired: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_REQUIRED', user, challengeName, challengeParameters });
      },
      mfaSetup: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_SETUP', user, challengeName, challengeParameters });
      },
      selectMFAType: (challengeName, challengeParameters) => {
        // For TOTP setup on first login
        user.sendMFASelectionAnswer('SOFTWARE_TOKEN_MFA', {
          onSuccess: () => {
            reject({ type: 'MFA_SETUP', user, challengeName, challengeParameters });
          },
          onFailure: reject
        });
      }
    });
  });
};

export const completeNewPassword = (user, newPassword, attributes = {}) => {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: async (result) => {
        try {
          await auth.getCredentials();
          auth.notify(user);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      },
      onFailure: reject,
      mfaSetup: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_SETUP', user, challengeName, challengeParameters });
      },
      totpRequired: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_REQUIRED', user, challengeName, challengeParameters });
      },
      mfaRequired: (challengeName, challengeParameters) => {
        reject({ type: 'MFA_REQUIRED', user, challengeName, challengeParameters });
      }
    });
  });
};

export const signOut = () => {
  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
    auth.clearCredentials();
    auth.notify(null);
  }
};

export const forgotPassword = (username) => {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: resolve,
      onFailure: reject
    });
  });
};

export const confirmPassword = (username, code, newPassword) => {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject
    });
  });
};

export const getCurrentUser = () => auth.getCurrentUser();
export const getSession = () => auth.getSession();
export const getToken = () => auth.getToken();
export const getCredentials = () => auth.getCredentials();
export const isAuthenticated = () => auth.isAuthenticated();
export const getUserGroups = () => auth.getUserGroups();
export const getCurrentRole = () => auth.getCurrentRole();
export const hasGroup = (groupName) => auth.hasGroup(groupName);
export const isAdmin = () => auth.isAdmin();
export const isResearcher = () => auth.isResearcher();
export const isUser = () => auth.isUser();
export const setupMFA = (user) => {
  return new Promise((resolve, reject) => {
    user.associateSoftwareToken({
      onSuccess: (result) => resolve(result),
      onFailure: reject,
      associateSecretCode: (secretCode) => {
        resolve({ secretCode });
      }
    });
  });
};

export const verifyMFASetup = (user, totpCode) => {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(totpCode, 'MFA Setup', {
      onSuccess: (result) => {
        // Clear credentials cache to force refresh
        auth.clearCredentials();
        // Don't notify yet - let the app handle the redirect
        resolve(result);
      },
      onFailure: reject
    });
  });
};

export const verifyMFA = (user, totpCode, rememberDevice = false) => {
  return new Promise((resolve, reject) => {
    // For TOTP MFA verification during login challenge
    user.sendMFACode(totpCode, {
      onSuccess: async (result) => {
        try {
          await auth.getCredentials();
          auth.notify(user);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      },
      onFailure: reject
    }, 'SOFTWARE_TOKEN_MFA');
  });
};

const rememberDeviceForUser = (user) => {
  return new Promise((resolve, reject) => {
    user.setDeviceStatusRemembered({
      onSuccess: resolve,
      onFailure: reject
    });
  });
};

export const checkMFAStatus = (user) => {
  return new Promise((resolve, reject) => {
    user.getUserMfaPreference({
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: reject
    });
  });
};

export const getUserEmail = (user) => {
  return new Promise((resolve, reject) => {
    user.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        const emailAttr = attributes.find(attr => attr.Name === 'email');
        resolve(emailAttr ? emailAttr.Value : null);
      }
    });
  });
};

export const onAuthChange = (callback) => auth.onAuthChange(callback);

export default auth;